/**
 * Editor PDF-layout simulation (canvas + preview_pdf context).
 *
 * The layout editor posts this payload as `context` (tickettype, pricing,
 * programmation, place, printer, salepoint) when no real simulator selection
 * is set — same channel as a chosen ticket type. Engine `previewPdf.js`
 * consumes it; its own fallbacks exist only when context has no ticket type
 * (e.g. a global default-layout sample).
 *
 * KEEP IN SYNC ticket ids / VDR / labels / owner placeholders /
 * `buildPreviewWindows` with engine/third/common/pdf/previewPdf.js
 * (`PREVIEW_*`, `OWNER_PLACEHOLDERS`).
 *
 * Also KEEP IN SYNC:
 * - `buildDocumentBodyPreviewLines` ↔ engine `previewPdf.buildDocumentBodyPreviewLines`
 * - `buildProgrammePreviewScreenings` / `buildProgrammePreviewUnits`
 *   ↔ engine `programmeBody.build_programme_preview_units`
 *
 * Hall / venue strings are i18n placeholders (`models.screening.place`,
 * `models.screening.venue`, …), not a real salle from the instance.
 */

import moment from 'moment';
import { canonical_pdf_layout_type } from './pdfLayoutType';

// UUIDv4: version nibble 4, RFC 4122 variant 8/9/a/b (not 1).
export const PREVIEW_TICKET_ID = '12345678-1234-4321-8234-123456789012';
export const PREVIEW_SCREENING_ID = 'preview-screening';

export const PREVIEW_BOOKING_ID = 'preview-booking';

/** zone:row:number — matches Engine ScreeningInfoWidget (`split(':')`) */
export const PREVIEW_SEAT = 'Parterre:5:12';

/**
 * Pick a mock booking seat from a hall / place / screening map.
 * Prefer `.label` (what real bookings store). KEEP IN SYNC with engine
 * `previewPdf.pickPreviewSeat`.
 *
 * @param {object} [source] screening, cinema_hall, or place
 * @returns {string} zone:row:number
 */
export function pickPreviewSeat(source) {
    const seats = source?.cinema_hall?.map?.seats
        || source?.map?.seats
        || [];
    if (!Array.isArray(seats) || seats.length === 0)
        return PREVIEW_SEAT;

    for (const seat of seats) {
        if (seat?.label)
            return String(seat.label);
        const placing = seat?.placing;
        if (!placing)
            continue;
        const zone = placing.zone ?? seat.zoneLabel;
        const row = placing.row;
        const number = placing.seat_number;
        if (zone != null && row != null && number != null)
            return `${zone}:${row}:${number}`;
    }

    return PREVIEW_SEAT;
}

export const PREVIEW_VDR_AUTH = {
    vdr_auth_serial: '123456',
    vdr_auth_key: 'abcdef',
};

/** Single-person tariff: the Nx quantity prefix is a niche case, not the mock. */
export const PREVIEW_PEOPLE = 1;

export const PREVIEW_TICKETTYPE_ID = 'preview-tickettype';

/** `models.tickettype.name` */
export const PREVIEW_TICKETTYPE_NAME = {
    fr: 'Nom du type de billet',
    en: 'Name of the ticket type',
    de: 'Name des Tickettyps',
    it: 'Nome del tipo di biglietto',
};

export const PREVIEW_TICKETTYPE_DESCRIPTION = {
    fr: 'La description du type de billet sera affichée ici',
    en: 'The ticket type description will be displayed here',
    de: 'Die Beschreibung des Tickettyps wird hier angezeigt',
    it: 'La descrizione del tipo di biglietto verrà visualizzata qui',
};

/** `models.pricing.name` */
export const PREVIEW_PRICING_NAME = {
    fr: 'Nom du tarif',
    en: 'Name of the pricing',
    de: 'Tarifname',
    it: 'Nome del prezzo',
};

export const PREVIEW_PRICE = 12;

export const PREVIEW_CURRENCY = 'CHF';

export const PREVIEW_VAT = 8.1;

/** `models.screening.title` */
export const PREVIEW_SCREENING_TITLE = {
    fr: 'Nom du film',
    en: 'Name of the film',
    de: 'Filmname',
    it: 'Nome del film',
};

/** `models.screening.*` — placeholders only, not a real cinema hall */
export const PREVIEW_PLACE = {
    fr: {
        _id: 'preview-place',
        cinema: "Nom de l'établissement",
        name: 'Nom de la salle',
        address: 'Adresse de la salle',
        zip: 'Zip',
        city: 'Ville',
        state: 'Canton',
        country: 'Pays',
    },
    en: {
        _id: 'preview-place',
        cinema: 'Name of establishment',
        name: 'Name of the venue',
        address: 'Address of the venue',
        zip: 'Zip',
        city: 'City',
        state: 'State',
        country: 'Country',
    },
    de: {
        _id: 'preview-place',
        cinema: 'Name der Einrichtung',
        name: 'Name des Saals',
        address: 'Adresse des Kinos',
        zip: 'PLZ',
        city: 'Stadt',
        state: 'Kanton',
        country: 'Land',
    },
    it: {
        _id: 'preview-place',
        cinema: 'Nome dello stabilimento',
        name: 'Nome del luogo',
        address: 'Indirizzo del luogo',
        zip: 'Cerniera',
        city: 'Città',
        state: 'Stato',
        country: 'Paese',
    },
};

export const OWNER_PLACEHOLDERS = {
    fr: {
        title: 'M.',
        company: 'Entreprise SA',
        firstname: 'Jean',
        lastname: 'Dupont',
        email: 'jean.dupont@example.com',
        age: '42',
        sex: 'M',
        phone: '+41 21 000 00 00',
        cellphone: '+41 79 000 00 00',
        language: 'Français',
        birthdate: '1970-01-01',
        address: {
            street: 'Rue Example 1',
            city: 'Lausanne',
            zip: '1000',
            country: 'Suisse',
        },
    },
    en: {
        title: 'Mr',
        company: 'Company Ltd',
        firstname: 'John',
        lastname: 'Doe',
        email: 'john.doe@example.com',
        age: '42',
        sex: 'M',
        phone: '+41 21 000 00 00',
        cellphone: '+41 79 000 00 00',
        language: 'English',
        birthdate: '1970-01-01',
        address: {
            street: 'Example Street 1',
            city: 'Lausanne',
            zip: '1000',
            country: 'Switzerland',
        },
    },
    de: {
        title: 'Herr',
        company: 'Firma AG',
        firstname: 'Hans',
        lastname: 'Muster',
        email: 'hans.muster@example.com',
        age: '42',
        sex: 'M',
        phone: '+41 21 000 00 00',
        cellphone: '+41 79 000 00 00',
        language: 'Deutsch',
        birthdate: '1970-01-01',
        address: {
            street: 'Beispielstrasse 1',
            city: 'Lausanne',
            zip: '1000',
            country: 'Schweiz',
        },
    },
};

/** `models.environment.printer` / `salepoint` */
export const PREVIEW_ENVIRONMENT = {
    printer: {
        fr: 'Nom du caissier',
        en: 'Name of cashier',
        de: 'Name des Kassierers',
        it: 'Nome del cassiere',
    },
    salepoint: {
        fr: 'Nom du point de vente',
        en: 'Name of point of sale',
        de: 'Name der Verkaufsstelle',
        it: 'Nome del punto vendita',
    },
};

export function previewLocalized(map, lang, fallbackLang = 'fr') {
    if (!map || typeof map !== 'object')
        return '';
    return map[lang] || map[fallbackLang] || map.en || Object.values(map)[0] || '';
}

/**
 * Match engine TicketInfoWidget.formatPricingAmount for the canvas mock.
 * Payload stores the numeric amount only; display formatting lives here.
 */
export function formatPreviewPrice(amount, currency = PREVIEW_CURRENCY, lang = 'fr') {
    const locale = lang === 'fr' ? 'fr-CH' : (lang === 'en' ? 'en-GB' : (lang === 'de' ? 'de-CH' : lang));
    const EN_DASH = '\u2013';
    let formatted = Number(amount).toLocaleString(locale, { style: 'currency', currency });
    if (currency === 'CHF')
        formatted = formatted.replace('.00', '.' + EN_DASH);
    return formatted.replace('\u00a0', ' ');
}

export function previewPlace(lang = 'fr') {
    return PREVIEW_PLACE[lang] || PREVIEW_PLACE.fr;
}

export function previewOwner(lang = 'fr') {
    return OWNER_PLACEHOLDERS[lang] || OWNER_PLACEHOLDERS.fr;
}

/**
 * Context posted by "download sample" on ticket-type layout mappings.
 * Engine `resolvePreviewContext` loads the type by id and the pricing by key
 * (or uses the inline `pricing` when the type has no pricings, e.g. OTP).
 */
export function build_pdf_sample_download_context({
    tickettypeId,
    pricingKey,
    pricing,
} = {}) {
    const context = {};

    if (tickettypeId)
        context.tickettype_id = tickettypeId;

    if (pricingKey)
        context.pricing_key = pricingKey;

    if (pricing && typeof pricing === 'object')
        context.pricing = { ...pricing };

    return context;
}

export function resolve_preview_pricing(tickettype, pricingKey, pricinglists = []) {
    if (!pricingKey)
        return null;

    const from_tickettype = tickettype?.pricings?.[pricingKey];
    if (from_tickettype)
        return from_tickettype;

    if (pricingKey.includes(':')) {
        const [list_id, key] = pricingKey.split(':');
        return pricinglists.find(list => list._id === list_id)?.pricings?.[key] || null;
    }

    for (const list of pricinglists || []) {
        if (list?.pricings?.[pricingKey])
            return list.pricings[pricingKey];
    }

    return null;
}

/**
 * Mirror Engine `expandTickettypeWindows` / tickettypes.expand_windows.
 */
export function expandTickettypeWindows(windows, now) {
    return (windows || []).map((w) => {
        const expw = { ...w };
        if ('duration' in w) {
            const start_at = moment(now).startOf('day');
            const stop_at = start_at.clone().add(moment.duration(w.duration));
            expw.nbookings = w.nbookings;
            expw.start_at = start_at.valueOf();
            expw.stop_at = stop_at.valueOf();
            delete expw.duration;
        }
        return expw;
    });
}

/** One calendar day of validity (OTP / fallback). */
export function buildDayValidityWindow(day) {
    const start_at = moment(day).startOf('day');
    return {
        start_at: start_at.valueOf(),
        stop_at: start_at.clone().add(1, 'day').valueOf(),
        nbookings: 1,
    };
}

function is_uuidv4(id) {
    return typeof id === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

/**
 * Real selected screening (UUID) → its day; otherwise purchase day (`now`).
 * Keep in sync with Engine `resolvePreviewValidityAnchor`.
 */
export function resolvePreviewValidityAnchor({ screening, programmation, now }) {
    const selected = screening || programmation;
    if (selected && is_uuidv4(selected._id || selected.id) && selected.start_at != null)
        return moment(selected.start_at);
    return moment(now);
}

/**
 * Canvas / PDF validity windows — keep in sync with Engine `buildPreviewWindows`.
 *
 * - OTP / empty windows: one day (chosen screening day, else today)
 * - Multi-pass: expand tickettype.windows like a purchase at `now`
 */
export function buildPreviewWindows({
    tickettype,
    screening,
    programmation,
    now = Date.now(),
} = {}) {
    const is_otp = tickettype?._id === 'one-time-pass';
    const anchor = resolvePreviewValidityAnchor({ screening, programmation, now });

    if (is_otp)
        return [buildDayValidityWindow(anchor)];

    const raw = tickettype?.windows;
    if (!Array.isArray(raw) || raw.length === 0)
        return [buildDayValidityWindow(anchor)];

    const selected = screening || programmation;
    return expandTickettypeWindows(raw, now).map((w) => {
        if (w.start_at != null && w.stop_at != null) {
            return {
                ...w,
                start_at: moment(w.start_at).valueOf(),
                stop_at: moment(w.stop_at).valueOf(),
            };
        }

        if (w.screening_id) {
            const day = (
                selected
                && (selected._id === w.screening_id || selected.id === w.screening_id)
                && selected.start_at != null
            )
                ? moment(selected.start_at)
                : moment(now);
            return { ...w, ...buildDayValidityWindow(day) };
        }

        return w;
    });
}

/**
 * Payload posted to Engine `/layout/preview_pdf` when the simulator has no
 * ticket type / pricing / screening selected. Canvas widgets use the same
 * objects (via `buildPdfCanvasPreviewProps` and editor fallbacks).
 */
export function buildPreviewSimulation(lang = 'fr') {
    return {
        tickettype: {
            _id: PREVIEW_TICKETTYPE_ID,
            name: { ...PREVIEW_TICKETTYPE_NAME },
            description: { ...PREVIEW_TICKETTYPE_DESCRIPTION },
            people: PREVIEW_PEOPLE,
        },
        pricing: {
            key: 'preview',
            name: { ...PREVIEW_PRICING_NAME },
            description: { fr: '', en: '', de: '', it: '' },
            price: { [PREVIEW_CURRENCY]: PREVIEW_PRICE },
            VAT: PREVIEW_VAT,
        },
        programmation: {
            title: { ...PREVIEW_SCREENING_TITLE },
            start_at: '1970-12-01T20:00:00.000Z',
            stop_at: '1970-12-01T22:30:00.000Z',
        },
        place: previewPlace(lang),
        printer: previewLocalized(PREVIEW_ENVIRONMENT.printer, lang),
        salepoint: previewLocalized(PREVIEW_ENVIRONMENT.salepoint, lang),
    };
}

/** Sample programme film titles — KEEP IN SYNC with engine programmeBody.js */
const PROGRAMME_PREVIEW_TITLES = {
    fr: [
        'Searching for Sugar Man',
        'Jean-Michel, Super Caribou',
        'Vaiana (Live Action)',
        'Rose',
        'Teenage Sex and Death at Camp Miasma',
        'Anatomy of a Fall',
        'The Zone of Interest',
        'Past Lives',
        'Killers of the Flower Moon',
        'Poor Things',
        'The Holdovers',
        'May December',
        'All of Us Strangers',
        'Perfect Days',
        'Fallen Leaves',
    ],
    en: [
        'Searching for Sugar Man',
        'Jean-Michel, Super Caribou',
        'Moana (Live Action)',
        'Rose',
        'Teenage Sex and Death at Camp Miasma',
        'Anatomy of a Fall',
        'The Zone of Interest',
        'Past Lives',
        'Killers of the Flower Moon',
        'Poor Things',
        'The Holdovers',
        'May December',
        'All of Us Strangers',
        'Perfect Days',
        'Fallen Leaves',
    ],
    de: [
        'Searching for Sugar Man',
        'Jean-Michel, Super Caribou',
        'Vaiana (Live Action)',
        'Rose',
        'Teenage Sex and Death at Camp Miasma',
        'Anatomy of a Fall',
        'The Zone of Interest',
        'Past Lives',
        'Killers of the Flower Moon',
        'Poor Things',
        'The Holdovers',
        'May December',
        'All of Us Strangers',
        'Perfect Days',
        'Fallen Leaves',
    ],
    it: [
        'Searching for Sugar Man',
        'Jean-Michel, Super Caribou',
        'Vaiana (Live Action)',
        'Rose',
        'Teenage Sex and Death at Camp Miasma',
        'Anatomy of a Fall',
        'The Zone of Interest',
        'Past Lives',
        'Killers of the Flower Moon',
        'Poor Things',
        'The Holdovers',
        'May December',
        'All of Us Strangers',
        'Perfect Days',
        'Fallen Leaves',
    ],
};

const PROGRAMME_WORKSHOP_TITLE = {
    fr: 'Atelier jeune public',
    en: 'Kids workshop',
    de: 'Kinderworkshop',
    it: 'Laboratorio ragazzi',
};

/**
 * Sample screenings for programme canvas + Engine preview_pdf context.
 * Sized so a 3-page first/continuation/last starter typically fills the PDF.
 * KEEP IN SYNC with engine `build_programme_preview_units`.
 */
export function buildProgrammePreviewScreenings(lang = 'fr') {
    const film_titles = PROGRAMME_PREVIEW_TITLES[lang] || PROGRAMME_PREVIEW_TITLES.en;
    const versions = ['ENf', 'D+F', 'D', 'Df', 'ENdf', 'F', 'VO', 'OmU'];
    const halls = ['Salle 1', 'Salle 2', 'Salle 3'];
    const base = moment().startOf('day').add(1, 'day').hour(10).minute(0);
    const screenings = [];

    // Sized for A3 (~34+38 cm body): 12 days × 6 slots → first+continuation+last in preview.
    // KEEP IN SYNC with engine `build_programme_preview_units`.
    for (let day = 0; day < 12; day++) {
        const dayBase = base.clone().add(day, 'days');
        const perDay = 6;
        for (let slot = 0; slot < perDay; slot++) {
            const title = film_titles[(day * 3 + slot) % film_titles.length];
            const start = dayBase.clone().add(slot * 95, 'minutes');
            screenings.push({
                start_at: start.toISOString(),
                stop_at: start.clone().add(100, 'minutes').toISOString(),
                title: { [lang]: title, original: title },
                version: versions[(day + slot) % versions.length],
                cinema_hall: { name: halls[(day + slot) % halls.length] },
            });
        }
        if (day === 1) {
            screenings.push({
                start_at: dayBase.clone().hour(10).toISOString(),
                stop_at: dayBase.clone().hour(13).toISOString(),
                title: { ...PROGRAMME_WORKSHOP_TITLE },
                version: 'D+F',
                cinema_hall: { name: 'Salle 2' },
            });
        }
    }

    return screenings.sort((a, b) => moment(a.start_at).valueOf() - moment(b.start_at).valueOf());
}

/**
 * Flat day/screening units for the ProgrammeBody canvas mock.
 * KEEP IN SYNC with engine `build_programme_units` / `build_programme_preview_units`.
 */
export function buildProgrammePreviewUnits(lang = 'fr', dayDateFormat = 'dddd D MMMM') {
    const previousLocale = moment.locale();
    moment.locale(lang);
    try {
        const screenings = buildProgrammePreviewScreenings(lang);
        const units = [];
        let lastDayKey = null;

        screenings.forEach((screening) => {
            const dayKey = moment(screening.start_at).format('YYYY-MM-DD');
            if (dayKey !== lastDayKey) {
                lastDayKey = dayKey;
                units.push({
                    type: 'day',
                    day_key: dayKey,
                    label: moment(screening.start_at).format(dayDateFormat),
                });
            }

            const localizedTitle = screening.title?.[lang]
                || screening.title?.original
                || screening.title?.fr
                || '';

            units.push({
                type: 'screening',
                day_key: dayKey,
                start_at: screening.start_at,
                stop_at: screening.stop_at || null,
                title: localizedTitle,
                version: screening.version || '',
                place: screening.cinema_hall?.name || '',
            });
        });

        return units;
    } finally {
        moment.locale(previousLocale);
    }
}

export function formatProgrammePreviewTime(unit, programme = {}) {
    if (!unit?.start_at)
        return '';
    const start = moment(unit.start_at).format(programme.time_format || 'HH:mm');
    if (!programme.columns?.time_range || !unit.stop_at)
        return start;
    const stop = moment(unit.stop_at).format(programme.time_format || 'HH:mm');
    if (stop === start)
        return start;
    return `${start}${programme.time_range_separator || ' > '}${stop}`;
}

/**
 * DocumentBody canvas / Engine preview lines.
 * KEEP IN SYNC with engine `previewPdf.buildDocumentBodyPreviewLines`.
 * Accepts legacy translated type labels via `canonical_pdf_layout_type`.
 * Samples are long enough for 3-page first/continuation/last starters.
 */
export function buildDocumentBodyPreviewLines(layoutType, lang = 'fr') {
    const type = canonical_pdf_layout_type(layoutType);
    const samples = {
        'cart-receipt': {
            fr: buildReceiptSampleLines('fr'),
            en: buildReceiptSampleLines('en'),
            de: buildReceiptSampleLines('de'),
            it: buildReceiptSampleLines('it'),
        },
        'ticket-bookings': {
            fr: buildBookingsSampleLines('fr'),
            en: buildBookingsSampleLines('en'),
            de: buildBookingsSampleLines('de'),
            it: buildBookingsSampleLines('it'),
        },
        closing: {
            fr: buildClosingSampleLines('fr'),
            en: buildClosingSampleLines('en'),
            de: buildClosingSampleLines('de'),
            it: buildClosingSampleLines('it'),
        },
    };

    const byType = samples[type];
    if (!byType)
        return [];
    return byType[lang] || byType.fr || byType.en || [];
}

const RECEIPT_ITEMS = {
    fr: [
        'Ticket Plein tarif', 'Popcorn', 'Boisson', 'Ticket Réduit',
        'Nachos', 'Glace', 'Ticket Abonné', 'Café', 'Menu famille',
        'Ticket Matinée', 'Bonbon', 'Ticket Groupe',
    ],
    en: [
        'Full price ticket', 'Popcorn', 'Drink', 'Reduced ticket',
        'Nachos', 'Ice cream', 'Subscriber ticket', 'Coffee', 'Family menu',
        'Matinee ticket', 'Candy', 'Group ticket',
    ],
    de: [
        'Ticket Vollpreis', 'Popcorn', 'Getränk', 'Ticket Reduziert',
        'Nachos', 'Eis', 'Aboticket', 'Kaffee', 'Familienmenü',
        'Matinee-Ticket', 'Süssigkeiten', 'Gruppenticket',
    ],
    it: [
        'Biglietto tariffa piena', 'Popcorn', 'Bevanda', 'Biglietto ridotto',
        'Nachos', 'Gelato', 'Biglietto abbonato', 'Caffè', 'Menu famiglia',
        'Biglietto matinée', 'Caramelle', 'Biglietto gruppo',
    ],
};

function buildReceiptSampleLines(lang) {
    const EN_DASH = '\u2013';
    const formatShort = (n) => {
        const value = Number(n);
        const centsTotal = Math.round(Math.abs(value) * 100 + Number.EPSILON);
        const units = Math.floor(centsTotal / 100);
        const cents = centsTotal % 100;
        const body = cents === 0
            ? `${units}.${EN_DASH}`
            : `${units}.${cents < 10 ? '0' : ''}${cents}`;
        return value < 0 ? `-${body}` : body;
    };
    const items = RECEIPT_ITEMS[lang] || RECEIPT_ITEMS.en;
    const lines = [];
    let total = 0;
    // ~45 articles → A4 first+continuation+last (28 only filled ~2 pages).
    for (let i = 0; i < 45; i++) {
        const price = 3.5 + (i % 9) * 1.5;
        total += price;
        lines.push(`1 x ${items[i % items.length]}`);
        lines.push(`                                             ${formatShort(price).padStart(5)}`);
    }
    lines.push('');
    const total_s = formatShort(total);
    const vat_s = formatShort(total * 0.081 / 1.081);
    if (lang === 'fr') {
        lines.push(`TOTAL TTC CHF                                   ${total_s}`);
        lines.push(`MONTANT TVA 8.1%: CHF ${vat_s}`);
        lines.push('');
        lines.push('Paiement : Espèces');
    } else if (lang === 'de') {
        lines.push(`TOTAL CHF (inkl. MWST)                          ${total_s}`);
        lines.push(`MWST BETRAG 8.1%: CHF ${vat_s}`);
        lines.push('');
        lines.push('Bezahlung: Bar');
    } else if (lang === 'it') {
        lines.push(`TOTALE IVA incl. CHF                            ${total_s}`);
        lines.push(`IMPORTO IVA 8.1%: CHF ${vat_s}`);
        lines.push('');
        lines.push('Pagamento: Contanti');
    } else {
        lines.push(`TOTAL incl. VAT CHF                             ${total_s}`);
        lines.push(`VAT AMOUNT 8.1%: CHF ${vat_s}`);
        lines.push('');
        lines.push('Payment: Cash');
    }
    return lines;
}

function buildBookingsSampleLines(lang) {
    const hall = { fr: 'Salle', en: 'Hall', de: 'Saal', it: 'Sala' }[lang] || 'Hall';
    const films = [
        'Film A', 'Film B', 'Film C', 'Film D', 'Film E',
        'Film F', 'Film G', 'Film H', 'Film I', 'Film J',
        'Film K', 'Film L', 'Film M', 'Film N', 'Film O',
    ];
    const lines = [];
    for (let i = 0; i < 18; i++) {
        const day = 15 + (i % 10);
        const hour = 14 + (i % 8);
        const h = 1 + (i % 3);
        if (lang === 'fr')
            lines.push(`• ${day}.08.2026 ${hour}h30 / ${hall} ${h}`);
        else
            lines.push(`• ${day}.08.2026 ${String(hour).padStart(2, '0')}:30 / ${hall} ${h}`);
        lines.push(films[i % films.length] + (i % 5 === 1
            ? (lang === 'fr' ? ' (2 réservations)'
                : lang === 'de' ? ' (2 Buchungen)'
                    : lang === 'it' ? ' (2 prenotazioni)'
                        : ' (2 bookings)')
            : ''));
        if (i % 5 === 1) {
            if (lang === 'fr') {
                lines.push('Parterre, Rangée : 5 / Siège : 12');
                lines.push('Parterre, Rangée : 5 / Siège : 13');
            } else if (lang === 'de') {
                lines.push('Parkett, Reihe: 5 / Platz: 12');
                lines.push('Parkett, Reihe: 5 / Platz: 13');
            } else if (lang === 'it') {
                lines.push('Platea, Fila: 5 / Posto: 12');
                lines.push('Platea, Fila: 5 / Posto: 13');
            } else {
                lines.push('Stalls, Row: 5 / Seat: 12');
                lines.push('Stalls, Row: 5 / Seat: 13');
            }
        }
        lines.push('');
    }
    return lines;
}

function buildClosingSampleLines(lang) {
    const salesLabels = {
        fr: ['Plein tarif', 'Réduit', 'Articles', 'Abonnement', 'Matinée', 'Groupe', 'VIP'],
        en: ['Full price', 'Reduced', 'Articles', 'Subscription', 'Matinee', 'Group', 'VIP'],
        de: ['Vollpreis', 'Reduziert', 'Artikel', 'Abo', 'Matinee', 'Gruppe', 'VIP'],
        it: ['Tariffa piena', 'Ridotto', 'Articoli', 'Abbonamento', 'Matinée', 'Gruppo', 'VIP'],
    };
    const labels = salesLabels[lang] || salesLabels.en;
    const header = {
        fr: [
            'Caisse : Caisse principale',
            'Session : 15.08.2026, 10h00 à 15.08.2026, 18h30',
            'Cloturée le : 15.08.2026, 18h35',
            'Vendeur(s) : Alice',
            '',
            'Total : CHF 275.50',
            '— dont CHF 180.– en espèces',
            '— dont CHF 95.50 avec TPE',
            '',
            'Ventes',
        ],
        en: [
            'Cashregister: Main till',
            'Session: 15.08.2026, 10:00 to 15.08.2026, 18:30',
            'Closed on: 15.08.2026, 18:35',
            'Seller(s): Alice',
            '',
            'Total: CHF 275.50',
            '— including CHF 180.– in cash',
            '— including CHF 95.50 by EFT',
            '',
            'Sales',
        ],
        de: [
            'Kasse: Hauptkasse',
            'Sitzung: 15.08.2026, 10:00 bis 15.08.2026, 18:30',
            'Geschlossen am: 15.08.2026, 18:35',
            'Verkäufer: Alice',
            '',
            'Total: CHF 275.50',
            '— davon CHF 180.– in bar',
            '— davon CHF 95.50 über Zahlungsterminal',
            '',
            'Verkäufe',
        ],
        it: [
            'Cassa: Cassa principale',
            'Sessione: 15.08.2026, 10:00 a 15.08.2026, 18:30',
            'Chiusa il: 15.08.2026, 18:35',
            'Venditore/i: Alice',
            '',
            'Totale: CHF 275.50',
            '— di cui CHF 180.– in contanti',
            '— di cui CHF 95.50 con TPE',
            '',
            'Vendite',
        ],
    };
    const lines = [...(header[lang] || header.en)];
    for (let i = 0; i < 36; i++) {
        const qty = String(1 + (i % 14)).padStart(2, ' ');
        lines.push(`${qty} x ${labels[i % labels.length]}`);
    }
    return lines;
}
